/**
 * BacklogScreen — Product Backlog (Fase 5 · Ciclos de Desenvolvimento).
 *
 * Fiel ao protótipo (StepBacklog): KPIs (Itens · Story Points · Histórias ·
 * Dependências) + árvore Épico→Feature→(História|Atividade Técnica) em accordion,
 * cada folha com painel de detalhe (critérios de aceite, reuso, dependências com
 * checagem de ordem de ciclo). Dados 100% da memória/contexto do projeto (GET
 * /backlog). "Gerar/Regerar com IA" chama o tech-lead (Claude).
 */
import { C } from '@/theme/palette';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProjects } from '@/api/projects';
import { useBacklog, useGenerateBacklog } from '@/api/backlog';
import { BACKLOG_TYPE, effectivePoints, flattenLeaves, backlogStats } from './backlog/backlogModel';
import { AiTruncationNotice } from '@/components/ui';

// Paleta: segue o tema global (aliases bg/surface/border/text/dim/muted… em theme/palette.js).

function Badge({ label, color }) {
  return (
    <span style={{ fontFamily: 'monospace', fontSize: 9.5, letterSpacing: '0.06em', padding: '2px 7px',
      borderRadius: 6, color, background: `${color}1F`, border: `1px solid ${color}44`, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

function Kpi({ label, value, sub, color }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, background: C.surface, borderRadius: 12, padding: 14 }}>
      <div style={{ fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color, marginTop: 4, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

/** Nó recursivo do backlog (épico/feature/folha). */
function Item({ node, kind, cycle, depth, cycleOfTitle }) {
  const [open, setOpen] = useState(depth === 0);
  const type = BACKLOG_TYPE[kind] || BACKLOG_TYPE.story;
  const isLeaf = kind === 'story' || kind === 'task';

  // Filhos + pontos efetivos agregados.
  const children = kind === 'epic' ? (node.features || []).map((f) => ({ node: f, kind: 'feature' }))
    : kind === 'feature' ? (node.stories || []).map((s) => ({ node: s, kind: s.type === 'task' ? 'task' : 'story' }))
    : [];
  const leafPts = isLeaf
    ? effectivePoints(node)
    : (kind === 'epic'
        ? (node.features || []).flatMap((f) => f.stories || [])
        : (node.stories || [])).reduce((a, s) => a + effectivePoints(s), 0);
  const rawPts = Number(node.points || 0);
  const reduced = isLeaf && node.reuse && effectivePoints(node) !== rawPts;

  return (
    <div style={{ marginLeft: depth * 18, marginTop: 8 }}>
      <div onClick={() => setOpen((o) => !o)}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 9, padding: '10px 12px',
          background: C.surface, border: `1px solid ${C.border}`, borderLeft: `3px solid ${type.color}`,
          borderRadius: 9 }}>
        <span style={{ color: C.muted, fontFamily: 'monospace', width: 12, transition: 'transform .15s',
          transform: open && !isLeaf ? 'rotate(90deg)' : 'none', opacity: isLeaf ? 0.25 : 1 }}>›</span>
        <Badge label={type.label} color={type.color} />
        {node.id && <span style={{ fontFamily: 'monospace', fontSize: 11, color: C.muted }}>{String(node.id).slice(0, 8)}</span>}
        <span style={{ fontSize: 13, color: C.text, fontWeight: kind === 'epic' ? 700 : 550,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{node.title}</span>
        {node.reuse && <Badge label="REUSO" color={C.quantum} />}
        {isLeaf && node.targetRepo && (
          <Badge label={String(node.targetRepo).split('/').pop()} color={C.cyber} />
        )}
        <span style={{ fontFamily: 'monospace', fontSize: 11, color: C.dim, whiteSpace: 'nowrap' }}>
          {reduced ? <><s style={{ color: C.muted }}>{rawPts}</s> → {leafPts} pts</> : `${leafPts} pts`}
        </span>
        {cycle != null && (
          <span style={{ fontFamily: 'monospace', fontSize: 10, padding: '2px 6px', borderRadius: 6,
            border: `1px solid ${C.border}`, color: C.muted }}>C{cycle}</span>
        )}
        {isLeaf && Array.isArray(node.deps) && node.deps.length > 0 && (
          <span title="tem dependências" style={{ color: C.cyber, fontSize: 12 }}>🔗</span>
        )}
      </div>

      {open && !isLeaf && children.map((c, i) => (
        <Item key={c.node.id || i} node={c.node} kind={c.kind} cycle={cycle} depth={depth + 1} cycleOfTitle={cycleOfTitle} />
      ))}

      {open && isLeaf && (
        <div style={{ marginLeft: 18, marginTop: 6, padding: '10px 12px', background: C.black,
          border: `1px solid ${C.border}`, borderRadius: 9 }}>
          {Array.isArray(node.acceptance) && node.acceptance.length > 0 && (
            <>
              <div style={{ fontSize: 9.5, letterSpacing: '0.08em', color: C.muted, marginBottom: 6 }}>CRITÉRIOS DE ACEITE</div>
              {node.acceptance.map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: 7, alignItems: 'flex-start', margin: '3px 0' }}>
                  <span style={{ color: C.cyber, flexShrink: 0 }}>✓</span>
                  <span style={{ fontSize: 12, color: C.dim }}>{a}</span>
                </div>
              ))}
            </>
          )}
          {(node.reuse || (Array.isArray(node.deps) && node.deps.length > 0)) && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: node.acceptance?.length ? 10 : 0, alignItems: 'center' }}>
              {node.reuse && <span style={{ fontSize: 11, color: C.quantum }}>♻ coberto por reuso da Biblioteca</span>}
              {(node.deps || []).map((d, i) => {
                const depCycle = cycleOfTitle?.get(String(d));
                const violates = depCycle != null && cycle != null && depCycle > cycle;
                const col = violates ? C.hot : C.cyber;
                return (
                  <span key={i} style={{ fontSize: 10.5, fontFamily: 'monospace', padding: '2px 8px', borderRadius: 6,
                    color: col, background: `${col}1A`, border: `1px solid ${col}44` }}>
                    depende de {String(d).slice(0, 32)}{depCycle != null ? ` (C${depCycle})` : ''}{violates ? ' ⚠ ordem' : ''}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function BacklogScreen({ projectId: wizardProjectId } = {}) {
  const [searchParams] = useSearchParams();
  const urlProject = searchParams.get('project');
  const { data: projectsRaw } = useProjects();
  const projects = useMemo(
    () => (Array.isArray(projectsRaw) ? projectsRaw : projectsRaw?.items ?? []),
    [projectsRaw],
  );
  const projectId = wizardProjectId || urlProject || projects?.[0]?.id || null;
  const { data, isLoading } = useBacklog(projectId);
  const generate = useGenerateBacklog(projectId);

  const epics = data?.epics ?? [];
  const stats = useMemo(() => backlogStats(epics), [epics]);
  // Mapa título→ciclo (para checar ordem de dependências entre ciclos).
  const cycleOfTitle = useMemo(() => {
    const m = new Map();
    flattenLeaves(epics).forEach((l) => { if (l.title) m.set(String(l.title), l.cycle); });
    return m;
  }, [epics]);

  if (!projectId) return <div style={{ padding: 40, color: C.dim }}>Nenhum projeto selecionado.</div>;

  return (
    <div className="sp-page" style={{ color: C.text }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 22, letterSpacing: '-0.02em' }}>Product Backlog</h2>
        <button onClick={() => generate.mutate()} disabled={generate.isPending}
          style={{ marginLeft: 'auto', cursor: generate.isPending ? 'default' : 'pointer',
            background: C.cyber, color: C.black, border: 'none', borderRadius: 8,
            padding: '9px 16px', fontSize: 13, fontWeight: 700, opacity: generate.isPending ? 0.6 : 1 }}>
          {generate.isPending ? 'Gerando backlog…' : epics.length ? '↻ Regerar com IA' : '✨ Gerar Backlog com IA'}
        </button>
      </div>
      <p style={{ color: C.dim, maxWidth: '80ch', marginTop: 10, fontSize: 13.5, lineHeight: 1.5 }}>
        Gerado pelos squads a partir do escopo aprovado: Épicos → Features → Histórias de Usuário e
        Atividades Técnicas. Todas as histórias já nascem completas (critérios de aceite, atividades
        técnicas e dependências) antes do <strong style={{ color: C.text }}>Gate de Escopo</strong> —
        os ciclos apenas executam. Itens cobertos por reuso têm pontos reduzidos; a estimativa de
        tokens e custo é consolidada no Gate de Escopo.
      </p>

      {generate.isError && (
        <div style={{ marginTop: 12, color: C.digital, fontSize: 13 }}>
          Falha: {String(generate.error?.message || generate.error)}
        </div>
      )}

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginTop: 18 }}>
        <Kpi label="Itens" value={stats.items || '—'} color={C.cyber}
          sub={`${stats.epics} épicos · ${stats.features} features · ${stats.stories} histórias · ${stats.tasks} ativ. técnicas`} />
        <Kpi label="Story Points" value={stats.totalPts || '—'} color={C.quantum} sub="pontos efetivos (com reuso)" />
        <Kpi label="Histórias de Usuário" value={stats.stories || '—'} color={C.neon} sub="todas com critérios de aceite" />
        <Kpi label="Dependências" value={stats.withDeps.length || '—'} color={C.digital} sub="pré-requisitos mapeados entre itens" />
      </div>

      {isLoading && <div style={{ marginTop: 20, color: C.dim }}>Carregando…</div>}
      {!isLoading && epics.length === 0 && (
        <div style={{ marginTop: 24, padding: 28, border: `1px dashed ${C.border}`, borderRadius: 12,
          color: C.dim, textAlign: 'center' }}>
          Sem backlog ainda. Clique em <strong style={{ color: C.text }}>Gerar Backlog com IA</strong>.
        </div>
      )}

      <AiTruncationNotice complete={data?.aiComplete} onRetry={() => generate.mutate()} />
      <div style={{ marginTop: 14 }}>
        {epics.map((ep, i) => (
          <Item key={ep.id || i} node={ep} kind="epic" cycle={ep.cycleNumber} depth={0} cycleOfTitle={cycleOfTitle} />
        ))}
      </div>
    </div>
  );
}
